import type { RefObject } from 'react';
import { useRef, useCallback, useMemo, useEffect } from 'react';
import type { BoxProps } from '@chakra-ui/react';
import { Box, Center } from '@chakra-ui/react';
import type { Client, FileMsgInfo, SessionStoreInstance } from '@halo/chat-sdk-react';
import {
  isFailedMessage,
  isFailedSentMessage,
  isSuccessMessage,
  MsgPayloadType,
  type Msg,
} from '@halo/chat-sdk-react';
import { tools, CircularProgress, getFileTypeIconUrl } from '@halo/ui-chat-react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import ArrowIcon from './top.svg?react';
import CancelIcon from './cancel.svg?react';

import './index.css';

enum Status {
  PREPARE,
  LOADING,
  SUCCESS,
  ERROR,
}

function useMessage({
  message,
  fileMsg,
  sessionStore,
}: {
  message: Msg;
  fileMsg?: FileMsgInfo;
  sessionStore: SessionStoreInstance;
}) {
  const { status, progress } = useMemo(() => {
    if (isFailedSentMessage(message.status)) return { status: Status.ERROR, progress: 0 };
    if (isFailedMessage(message.status)) return { status: Status.PREPARE, progress: 0 };
    if (!fileMsg && isSuccessMessage(message.status))
      return { status: Status.PREPARE, progress: 0 };
    if (fileMsg?.progress !== 100)
      return { status: Status.LOADING, progress: fileMsg?.progress ?? 0 };
    if (fileMsg?.file) return { status: Status.SUCCESS, progress: 100 };
    if (!fileMsg?.file && !fileMsg?.process) return { status: Status.PREPARE, progress: 0 };
    return { status: Status.LOADING, progress: fileMsg?.progress ?? 0 };
  }, [message.status, fileMsg]);

  const cancel = useCallback(() => {
    sessionStore.getState().cancelFile(message.cli_msg_id);
  }, [message.cli_msg_id, sessionStore]);

  return { status, progress, cancel };
}

function Mask({
  onClick,
  children,
}: {
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (onClick) return onClick(e);
      e.stopPropagation();
      return;
    },
    [onClick]
  );

  return (
    <Center position="absolute" top="0" left="0" right="0" bottom="0" onClick={handleClick}>
      {children}
    </Center>
  );
}

function FileMask({
  message,
  sessionStore,
  fileMsg,
  maskRef,
  ...props
}: {
  message: Msg;
  sessionStore: SessionStoreInstance;
  fileMsg?: FileMsgInfo;
  maskRef?: RefObject<HTMLElement | null>;
} & Omit<BoxProps, 'children'>) {
  const { t } = useTranslation();
  const { status, progress, cancel } = useMessage({
    message,
    fileMsg,
    sessionStore,
  });

  const handleDownload = useCallback(() => {
    sessionStore
      .getState()
      .downloadFile(message)
      .then(() => {
        tools.toaster.success({
          title: t('tips.downloadCompleted'),
        });
      })
      .catch(() => {
        tools.toaster.error({
          title: t('tips.downloadFailed'),
        });
      });
  }, [message, sessionStore, t]);

  const nodeRef = useRef<HTMLElement>(null);

  const isHovering = useHover(maskRef);

  useEffect(() => {
    if (!maskRef?.current) return;
    maskRef.current.style.display = status === Status.PREPARE ? 'block' : 'none';
  }, [status, maskRef]);

  useEventListener(
    'click',
    (e) => {
      if (status !== Status.PREPARE) return;
      e.stopPropagation();
      e.preventDefault();
      handleDownload();
    },
    { target: maskRef }
  );

  const children = useMemo(() => {
    if (status === Status.LOADING)
      return (
        <Center
          position="relative"
          cursor="pointer"
          borderRadius="full"
          overflow="hidden"
          bg="blackAlpha.50"
          {...props}
          onClick={cancel}
        >
          <Box position="absolute" boxSize="full" p="1px">
            <CircularProgress value={Math.max(progress, 2)} animation="spin 1s linear infinite" />
          </Box>
          <CancelIcon />
        </Center>
      );
    if (status === Status.PREPARE)
      return (
        <Center
          cursor="pointer"
          {...props}
          w="full"
          h="full"
          css={{
            '&:hover': { opacity: 1 },
            backgroundImage: `url(${getFileTypeIconUrl(message.payload?.ext)})`,
            backgroundSize: '100000%',
            backgroundPosition: 'center',
          }}
          borderRadius="lg"
          onClick={handleDownload}
          opacity={isHovering ? '1' : '0'}
          transitionDuration="100ms"
          transitionProperty="opacity"
        >
          <ArrowIcon />
        </Center>
      );

    return <Box />;
  }, [status, progress, message, props, cancel, handleDownload, isHovering]);

  return (
    <Mask
      onClick={(e) => {
        if (status === Status.SUCCESS) return;
        e.stopPropagation();
      }}
    >
      <SwitchTransition>
        <CSSTransition
          key={status}
          nodeRef={nodeRef}
          classNames="zoom"
          addEndListener={(done: () => void) => {
            nodeRef.current?.addEventListener('transitionend', done, false);
          }}
        >
          <Center ref={nodeRef} h="full" w="full">
            {children}
          </Center>
        </CSSTransition>
      </SwitchTransition>
    </Mask>
  );
}

function ImageMask({
  message,
  sessionStore,
  fileMsg,
  ...props
}: { message: Msg; sessionStore: SessionStoreInstance; fileMsg?: FileMsgInfo } & Omit<
  BoxProps,
  'children'
>) {
  const { t } = useTranslation();
  const { status, progress, cancel } = useMessage({
    message,
    fileMsg,
    sessionStore,
  });

  const onceRef = useRef<boolean>(false);
  useEffect(() => {
    if (status !== Status.PREPARE) return;
    if (!isSuccessMessage(message.status)) return;
    if (onceRef.current) return;
    onceRef.current = true;
    sessionStore.getState().getFile(message);
  }, [status, sessionStore, message]);

  const nodeRef = useRef<HTMLElement>(null);

  const children = useMemo(() => {
    if (status === Status.LOADING)
      return (
        <Center
          position="relative"
          cursor="pointer"
          borderRadius="full"
          overflow="hidden"
          bg="blackAlpha.50"
          {...props}
          onClick={cancel}
        >
          <Box position="absolute" boxSize="full" p="1px">
            <CircularProgress value={Math.max(progress, 2)} animation="spin 1s linear infinite" />
          </Box>
          <CancelIcon />
        </Center>
      );
    if (status === Status.PREPARE)
      return (
        <Center
          position="relative"
          cursor="pointer"
          borderRadius="full"
          overflow="hidden"
          bg="blackAlpha.50"
          {...props}
          onClick={() => {
            sessionStore
              .getState()
              .getFile(message)
              .catch(() => {
                tools.toaster.error({
                  title: t('tips.downloadFailed'),
                });
              });
          }}
        >
          <ArrowIcon />
        </Center>
      );

    return <Box />;
  }, [status, progress, sessionStore, message, props, cancel, t]);

  return (
    <Mask
      onClick={(e) => {
        if (status === Status.SUCCESS) return;
        e.stopPropagation();
      }}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={status}
          nodeRef={nodeRef}
          classNames="zoom"
          addEndListener={(done: () => void) => {
            nodeRef.current?.addEventListener('transitionend', done, false);
          }}
        >
          <Center ref={nodeRef} h="full" w="full">
            {children}
          </Center>
        </CSSTransition>
      </SwitchTransition>
    </Mask>
  );
}

export function MessageUpload({
  message,
  sessionStore,
  ...props
}: {
  message: Msg;
  sessionStore?: SessionStoreInstance | null;
  client?: Client;
  fileMsg?: FileMsgInfo;
  maskRef?: RefObject<HTMLElement | null>;
} & Omit<BoxProps, 'children'>) {
  if (!sessionStore) return null;
  if (message.payload_type === MsgPayloadType.FILE)
    return <FileMask message={message} sessionStore={sessionStore} {...props} />;
  if (message.payload_type === MsgPayloadType.IMAGE)
    return <ImageMask message={message} sessionStore={sessionStore} {...props} />;
  return null;
}
